import React, { useState } from 'react';
import { set, get, has, reduce, reject } from 'lodash/fp';
import PropTypes, { number } from 'prop-types';
import { FlexboxGrid, Table, Col, IconButton, Icon, Checkbox } from 'rsuite';
import { useSelector, useDispatch } from 'react-redux';
import { addPosOrderItem } from '../../../../redux/sagas/routines';
import { addItemAdjustment } from '../../../../redux/POS/actions';

import { parseInt } from '../../../../helpers/utils';
import { OrderType } from '../../../../interfaces/pos';
import { COMPLETED } from '../../../../constants/pos/OrderStatus';
import createOrderItemKey from '../../../../helpers/pos/createOrderItemKey';
import applyOrderItemsAdjustment from '../../../../helpers/pos/applyOrderAdjustment';
import { getIsSavingItem } from '../../../../redux/POS/selectors/order';
import { getPosOrderAdjustment } from '../../../../redux/POS/selectors/orderAdjustment';
import toInventoryItemUniqKey from '../../../../helpers/toInventoryItemUniqKey';
import { NameCell } from '../../../../components/NameCell';

const { Column, HeaderCell, Cell } = Table;

export const ActionCell = ({
  onAddItem,
  isAddingItem,
  isExistingItem,
  dataKey,
  rowData,
  ...props
}) => {
  const itemId = get('item.id', rowData);
  const locationId = get('location.id', rowData);
  const zoneId = get('zone.id', rowData);
  const isAvailable = get('quantity', rowData) > 0;
  const handleAddItem = () => {
    if (isAvailable) {
      onAddItem(rowData);
    }
  };
  const isExisting = isExistingItem(itemId, locationId, zoneId);
  const disabledAdd =
    !isAvailable || isExisting || isAddingItem(itemId, locationId, zoneId);
  return (
    <Cell {...props} className="link-group">
      <IconButton
        disabled={disabledAdd}
        icon={
          <Icon
            icon="cart-plus"
            style={{ fontSize: 20, width: 31, padding: '3px 0' }}
          />
        }
        color="green"
        appearance="ghost"
        onClick={handleAddItem}
      />
    </Cell>
  );
};

ActionCell.propTypes = {
  onAddItem: PropTypes.func.isRequired,
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.shape({}),
  isAddingItem: PropTypes.func,
  isExistingItem: PropTypes.func
};

ActionCell.defaultProps = {
  rowData: null,
  isAddingItem: () => false,
  isExistingItem: () => false
};

const AvailableQtyCell = ({ dataKey, rowData, ...props }) => (
  <Cell {...props}>
    <div>
      <center>{rowData[dataKey] - get('onholdQuantity', rowData)}</center>
    </div>
  </Cell>
);

AvailableQtyCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: PropTypes.shape({
    quantity: number,
    onholdQuantity: number
  })
};

AvailableQtyCell.defaultProps = {
  rowData: null
};

const composeItemUniqKeys = reduce(
  (all, orderItem) => set(createOrderItemKey(orderItem), true, all),
  {}
);

if (localStorage.getItem('posShowZeroQty') === null)
  localStorage.setItem('posShowZeroQty', 1);

const defaultShowZeroQty = localStorage.getItem('posShowZeroQty');

function SearchResults({ order, isLoading, data }) {
  const dispatch = useDispatch();
  const [itemToAdd, setItemToAdd] = useState(null);
  const [showZeroQty, setShowZeroQty] = useState(defaultShowZeroQty);
  const isCompleted = parseInt(get('status', order)) === COMPLETED;
  const isSavingItem = useSelector(getIsSavingItem);
  const orderAdjustment = useSelector(getPosOrderAdjustment);
  const existingItemsByUniqKey = composeItemUniqKeys(
    applyOrderItemsAdjustment(order.items, orderAdjustment)
  );

  const handleAddItem = itemAvailability => {
    const quantity = 1;
    const orderId = order.id;
    setItemToAdd(itemAvailability);
    if (isCompleted) {
      dispatch(addItemAdjustment({ orderId, itemAvailability, quantity }));
    } else {
      const itemId = get('item.id', itemAvailability);
      const locationId = get('location.id', itemAvailability);
      const locationZoneId = get('zone.id', itemAvailability);
      const payload = {
        orderId,
        body: { itemId, locationId, locationZoneId, quantity }
      };
      dispatch(addPosOrderItem(payload));
    }
  };

  const handleToggleShowZeroQty = () => {
    const show = showZeroQty === '1' ? '0' : '1';
    localStorage.setItem('posShowZeroQty', show);
    setShowZeroQty(show);
  };

  const isAddingItem = (itemId, locationId, zoneId) => {
    const isMatchItemId = get('item.id', itemToAdd) === itemId;
    const isMatchLocationId = get('location.id', itemToAdd) === locationId;

    const isMatchLocation = zoneId
      ? isMatchLocationId && get('zone.id', itemToAdd) === zoneId
      : isMatchLocationId;

    return isSavingItem && isMatchItemId && isMatchLocation;
  };

  const isExistingItem = (itemId, locationId, zoneId) => {
    const uniqKey = toInventoryItemUniqKey(itemId, locationId, zoneId);
    return has(uniqKey, existingItemsByUniqKey);
  };

  const filteredData =
    showZeroQty === '1' ? data : reject({ quantity: 0 }, data);

  return (
    <FlexboxGrid style={{ marginBottom: 10 }}>
      <FlexboxGrid.Item componentClass={Col} xs={24}>
        <h6 style={{ margin: '10px 0 10px' }}>
          Search Result
          <Checkbox
            checked={showZeroQty === '1'}
            style={{
              top: 2,
              right: 8,
              width: 180,
              position: 'absolute',
              fontSize: 14
            }}
            onChange={handleToggleShowZeroQty}
          >
            Show 0 Quantity Items
          </Checkbox>
        </h6>
        <Table
          data={filteredData}
          loading={isLoading}
          rowHeight={60}
          autoHeight
          bordered
        >
          <Column flexGrow={1} align="left">
            <HeaderCell>
              <strong>Item</strong>
            </HeaderCell>
            <NameCell dataKey="item.name" />
          </Column>
          <Column align="right">
            <HeaderCell>
              <strong>Qty Available</strong>
            </HeaderCell>
            <AvailableQtyCell dataKey="quantity" />
          </Column>
          <Column align="center">
            <HeaderCell>
              <strong>Add Item</strong>
            </HeaderCell>
            <ActionCell
              dataKey="id"
              onAddItem={handleAddItem}
              isAddingItem={isAddingItem}
              isExistingItem={isExistingItem}
            />
          </Column>
        </Table>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
}

SearchResults.propTypes = {
  order: OrderType.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({}))
};

SearchResults.defaultProps = {
  data: []
};

export default SearchResults;
